import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTaxi, FaLock, FaUser, FaEye, FaEyeSlash, FaSpinner, FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import axios from "axios";
import { loginDriver, reset } from "../../features/driver/driverAuthSlice";
import { getFcmToken } from '../../firebase/getFcmToken';
import { API_URL } from '../../config/apiConfig';

const DriverLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [promoImages, setPromoImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { email, password } = formData;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { driver, isLoading, isError, isSuccess, isPendingApproval, message } =
    useSelector((state) => state.driverAuth);

  // Fetch driver login promotional images
  useEffect(() => {
    const fetchPromoImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'driver_login' }
        });
        if (response.data.success && response.data.data.length > 0) {
          setPromoImages(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching driver login images:", error);
      }
    };
    fetchPromoImages();
  }, []);

  // Auto-rotate images
  useEffect(() => {
    if (promoImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % promoImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [promoImages]);

  useEffect(() => {
    if (driver && driver._id && isSuccess) {
      navigate("/driver/dashboard", { replace: true });
    }
  }, [driver, navigate, isSuccess]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(reset());
    }
    if (isPendingApproval && message) {
      toast.warning(message);
      dispatch(reset());
    }
  }, [isError, isSuccess, isPendingApproval, message, driver, navigate, dispatch]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    let fcmToken = null;
    try {
      fcmToken = await getFcmToken();
    } catch (err) {
      console.warn('Failed to get FCM token for driver login:', err);
    }
    const userData = {
      email: email.trim().toLowerCase(),
      password: password.trim(),
      fcmToken,
    };
    dispatch(loginDriver(userData));
  };

  return (
    <div className="min-h-screen bg-[#F2F2F2] flex items-center justify-center p-4 py-12">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[1000px] flex flex-col md:flex-row overflow-hidden relative min-h-[550px]">
        {/* Left Side - Promo Image */}
        <div className="hidden md:block w-[45%] relative bg-[#000] overflow-hidden">
          {promoImages.length > 0 ? (
            <div className="absolute inset-0">
              <img
                src={promoImages[currentImageIndex].imageUrl}
                alt={promoImages[currentImageIndex].title}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-12 left-10 right-10 text-white">
                <h3 className="text-3xl font-black mb-4 leading-tight">
                  {promoImages[currentImageIndex].title}
                </h3>
                <p className="text-sm opacity-90 leading-relaxed font-medium">
                  {promoImages[currentImageIndex].description}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
               <FaSpinner className="animate-spin text-white text-4xl" />
            </div>
          )}
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-[55%] p-8 sm:p-12 md:p-16 flex flex-col justify-center relative">
          <button 
            onClick={() => navigate("/")}
            className="absolute top-8 left-8 text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-2 text-xs font-black tracking-widest"
          >
            <FaArrowLeft /> BACK TO HOME
          </button>

          <div className="mb-10 text-center md:text-left">
            <h1 className="text-4xl font-black text-gray-900 mb-3 tracking-tight">Driver Partner Login</h1>
            <p className="text-gray-500 font-bold tracking-wide uppercase text-xs">Access your partner dashboard</p>
          </div>

          {isPendingApproval && (
            <div className="mb-8 p-5 bg-orange-50/50 border border-orange-100 rounded-2xl flex items-start gap-3 animate-pulse">
              <div className="mt-1 text-orange-600"><FaShieldAlt size={20} /></div>
              <div>
                <p className="text-sm font-black text-orange-900 mb-1">Account Pending Approval</p>
                <p className="text-[11px] text-orange-700 leading-relaxed font-medium">Your profile is being reviewed by our team. This usually takes 24-48 hours. We'll notify you once it's active.</p>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-8 animate-fadeIn">
            <div className="group">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-blue-600 transition-colors">
                Partner Email
              </label>
              <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all duration-300 pb-1">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-600 transition-colors">
                  <FaUser size={18} />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={onChange}
                  className="w-full pl-8 py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                  placeholder="Enter your registered email"
                  required
                />
              </div>
            </div>

            <div className="group">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-blue-600 transition-colors">
                Secure Password
              </label>
              <div className="relative border-b-2 border-gray-100 group-focus-within:border-blue-600 transition-all duration-300 pb-1">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-600 transition-colors">
                  <FaLock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={onChange}
                  className="w-full pl-8 pr-10 py-2 bg-transparent text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300 hover:text-blue-600"
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/driver/forgot-password/send-otp" className="text-[10px] font-black text-blue-600 tracking-widest uppercase hover:underline">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-5 rounded-full font-black text-lg tracking-[2px] shadow-xl transition-all duration-300 active:scale-95 ${
                isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-gray-900 to-black text-white hover:shadow-2xl"
              }`}
            >
              {isLoading ? <FaSpinner className="animate-spin mx-auto text-2xl" /> : "SIGN IN"}
            </button>

            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
              <div className="relative flex justify-center text-[10px]"><span className="px-4 bg-white text-gray-400 font-bold tracking-widest uppercase">New Partner?</span></div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/driver/register")}
              className="w-full py-4 rounded-full border-2 border-gray-100 font-black text-gray-400 text-xs tracking-widest hover:border-blue-600 hover:text-blue-600 transition-all duration-300"
            >
              BECOME A DRIVER PARTNER
            </button>
          </form>

          <div className="mt-12 text-center text-[10px] font-bold text-gray-400 uppercase tracking-[2px]">
            Partner Support: support@cabzigo.com
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLogin;
